/**
 * The names of the color groups.
 */
export type StashPrimaryColorGroup =
  | 'blue'
  | 'green'
  | 'ice'
  | 'orange'
  | 'purple'
  | 'red'
  | 'royal'
  | 'seafoam'
  | 'teal'
  | 'yellow';

/**
 * Complete list of Stash brand colors & their shades.
 */
export enum StashColors {
  Purple900 = 'purple-900',
  Purple800 = 'purple-800',
  Purple700 = 'purple-700',
  Purple600 = 'purple-600',
  Purple500 = 'purple-500',
  Purple400 = 'purple-400',
  Purple300 = 'purple-300',
  Purple200 = 'purple-200',
  Purple100 = 'purple-100',

  Royal900 = 'royal-900',
  Royal800 = 'royal-800',
  Royal700 = 'royal-700',
  Royal600 = 'royal-600',
  Royal500 = 'royal-500',
  Royal400 = 'royal-400',
  Royal300 = 'royal-300',
  Royal200 = 'royal-200',
  Royal100 = 'royal-100',

  Blue900 = 'blue-900',
  Blue800 = 'blue-800',
  Blue700 = 'blue-700',
  Blue600 = 'blue-600',
  Blue500 = 'blue-500',
  Blue400 = 'blue-400',
  Blue300 = 'blue-300',
  Blue200 = 'blue-200',
  Blue100 = 'blue-100',

  Teal900 = 'teal-900',
  Teal800 = 'teal-800',
  Teal700 = 'teal-700',
  Teal600 = 'teal-600',
  Teal500 = 'teal-500',
  Teal400 = 'teal-400',
  Teal300 = 'teal-300',
  Teal200 = 'teal-200',
  Teal100 = 'teal-100',

  Green900 = 'green-900',
  Green800 = 'green-800',
  Green700 = 'green-700',
  Green600 = 'green-600',
  Green500 = 'green-500',
  Green400 = 'green-400',
  Green300 = 'green-300',
  Green200 = 'green-200',
  Green100 = 'green-100',

  Seafoam900 = 'seafoam-900',
  Seafoam800 = 'seafoam-800',
  Seafoam700 = 'seafoam-700',
  Seafoam600 = 'seafoam-600',
  Seafoam500 = 'seafoam-500',
  Seafoam400 = 'seafoam-400',
  Seafoam300 = 'seafoam-300',
  Seafoam200 = 'seafoam-200',
  Seafoam100 = 'seafoam-100',

  Yellow900 = 'yellow-900',
  Yellow800 = 'yellow-800',
  Yellow700 = 'yellow-700',
  Yellow600 = 'yellow-600',
  Yellow500 = 'yellow-500',
  Yellow400 = 'yellow-400',
  Yellow300 = 'yellow-300',
  Yellow200 = 'yellow-200',
  Yellow100 = 'yellow-100',

  Orange900 = 'orange-900',
  Orange800 = 'orange-800',
  Orange700 = 'orange-700',
  Orange600 = 'orange-600',
  Orange500 = 'orange-500',
  Orange400 = 'orange-400',
  Orange300 = 'orange-300',
  Orange200 = 'orange-200',
  Orange100 = 'orange-100',

  Red900 = 'red-900',
  Red800 = 'red-800',
  Red700 = 'red-700',
  Red600 = 'red-600',
  Red500 = 'red-500',
  Red400 = 'red-400',
  Red300 = 'red-300',
  Red200 = 'red-200',
  Red100 = 'red-100',

  Ice900 = 'ice-900',
  Ice800 = 'ice-800',
  Ice700 = 'ice-700',
  Ice600 = 'ice-600',
  Ice500 = 'ice-500',
  Ice400 = 'ice-400',
  Ice300 = 'ice-300',
  Ice200 = 'ice-200',
  Ice100 = 'ice-100',

  White = 'white',
  Black = 'black',
}

export type StashColor = `${StashColors}`;

/**
 * A limited list of stash brand colors, only including their 100, 500, and 700 shades.
 */
export enum StashCommonColors {
  Purple700 = StashColors.Purple700,
  Purple500 = StashColors.Purple500,
  Purple100 = StashColors.Purple100,

  Royal700 = StashColors.Royal700,
  Royal500 = StashColors.Royal500,
  Royal100 = StashColors.Royal100,

  Blue700 = StashColors.Blue700,
  Blue500 = StashColors.Blue500,
  Blue100 = StashColors.Blue100,

  Teal700 = StashColors.Teal700,
  Teal500 = StashColors.Teal500,
  Teal100 = StashColors.Teal100,

  Green700 = StashColors.Green700,
  Green500 = StashColors.Green500,
  Green100 = StashColors.Green100,

  Seafoam700 = StashColors.Seafoam700,
  Seafoam500 = StashColors.Seafoam500,
  Seafoam100 = StashColors.Seafoam100,

  Yellow700 = StashColors.Yellow700,
  Yellow500 = StashColors.Yellow500,
  Yellow100 = StashColors.Yellow100,

  Orange700 = StashColors.Orange700,
  Orange500 = StashColors.Orange500,
  Orange100 = StashColors.Orange100,

  Red700 = StashColors.Red700,
  Red500 = StashColors.Red500,
  Red100 = StashColors.Red100,

  Ice700 = StashColors.Ice700,
  Ice500 = StashColors.Ice500,
  Ice200 = StashColors.Ice200, // This is the one outlier, but it's used purposefully in a few places
  Ice100 = StashColors.Ice100,

  White = 'white',
  Black = 'black',
}

export type StashCommonColor = `${StashCommonColors}`;

/**
 * A limited list of stash brand colors, only including their primary (500) shade.
 */
export enum StashPrimaryColors {
  Blue500 = 'blue-500',
  Green500 = 'green-500',
  Ice500 = 'ice-500',
  Orange500 = 'orange-500',
  Purple500 = 'purple-500',
  Red500 = 'red-500',
  Royal500 = 'royal-500',
  Seafoam500 = 'seafoam-500',
  Teal500 = 'teal-500',
  Yellow500 = 'yellow-500',
}

export type StashPrimaryColor = `${StashPrimaryColors}`;
